<template>
  <div class="flow-graph-canvas" ref="container" @wheel.prevent="handleWheel" @mousedown="handleMouseDown"
       @touchstart="handleTouchStart" @touchmove.prevent="handleTouchMove" @touchend="handleTouchEnd">
    <div v-if="loading" class="loading-overlay">
      <div class="spinner"></div>
      <p>Chargement du graphe...</p>
    </div>

    <div v-else-if="error" class="error-overlay">
      <p>{{ error }}</p>
      <button @click="fetchGraph" class="retry-btn">Réessayer</button>
    </div>

    <!-- Canvas Layer -->
    <div
      class="transform-layer"
      :style="{ transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})` }"
    >
      <!-- Edges Layer (SVG) -->
      <svg class="edges-layer">
        <defs>
          <marker id="arrow-peelo" markerWidth="10" markerHeight="10" refX="7" refY="4" orient="auto" markerUnits="userSpaceOnUse">
            <path d="M0,0 L8,4 L0,8 Z" fill="#143028" />
          </marker>
        </defs>
        <g v-for="edge in edges" :key="edge.id">
          <path
            :d="getEdgePath(edge)"
            class="edge-path"
            :class="edge.connType"
            marker-end="url(#arrow-peelo)"
            fill="none"
          />
        </g>
      </svg>

      <!-- Edge Labels Layer (HTML) -->
      <div class="edge-labels-layer">
        <div
          v-for="edge in edges"
          :key="'lbl-' + edge.id"
          class="edge-label-chip"
          :class="edge.connType"
          :style="getEdgeLabelStyle(edge)"
        >
          <span class="edge-label-icon" v-if="edge.connType === 'redirection'">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 14 20 9 15 4"></polyline><path d="M4 20v-7a4 4 0 0 1 4-4h12"></path></svg>
          </span>
          <span class="edge-label-text">{{ edge.label || (edge.connType === 'redirection' ? 'Redirection' : 'Bouton') }}</span>
          <span v-if="edge.assignment" class="edge-var">{{ edge.assignment }}</span>
        </div>
      </div>

      <!-- Nodes Layer -->
      <div
        v-for="node in nodes"
        :key="node.id"
        class="node-item"
        :class="[node.data.nodeType, { 'selected': selectedNodeId === node.id }]"
        :data-node-id="node.id"
        :style="{ left: `${node.position.x}px`, top: `${node.position.y}px` }"
        @mousedown.stop="startDragNode($event, node)"
        @dblclick.stop="openNode(node)"
      >
        <!-- Badge -->
        <div class="node-badge" :class="node.data.nodeType">
          <span class="node-badge-dot"></span>
          <span>{{ nodeTypeLabel(node.data.nodeType) }}</span>
        </div>

        <div class="node-header">
           <div class="node-header-left">
             <span class="node-icon" :class="node.data.nodeType" v-html="getNodeIcon(node.data.nodeType)"></span>
             <span class="node-label">{{ node.data.label || 'Bloc sans nom' }}</span>
           </div>
           <button class="node-open-btn" @click.stop="openNode(node)" title="Ouvrir le flux">⋮</button>
        </div>

        <div class="node-body">
           <p class="node-desc" v-if="node.data.description || node.data.preview">
             {{ node.data.description || node.data.preview }}
           </p>

           <!-- Variables du bloc -->
           <div class="node-vars" v-if="node.data.variables && node.data.variables.length">
             <span class="vars-label">Variables</span>
             <div class="vars-chips">
               <span v-for="v in node.data.variables.slice(0, 5)" :key="v" class="var-chip">{{ v }}</span>
               <span v-if="node.data.variables.length > 5" class="var-chip var-more">+{{ node.data.variables.length - 5 }}</span>
             </div>
           </div>

           <div class="node-footer-tags" v-if="node.data.flowElementsCount !== undefined">
             <div class="footer-tag">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/></svg>
                <span>{{ node.data.flowElementsCount }} élément{{ node.data.flowElementsCount > 1 ? 's' : '' }}</span>
             </div>
           </div>
        </div>

        <!-- Connection Ports -->
        <div class="node-port port-in" v-if="node.data.nodeType !== 'startingPoint'"></div>
        <div class="node-port port-out"></div>
      </div>
    </div>

    <!-- Auto-organisation -->
    <div class="layout-actions">
      <button class="auto-layout-btn" @click="autoLayout" title="Réorganise les blocs en arbre à partir du point d'entrée">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 3l1.6 4.9a2 2 0 0 0 1.3 1.3L20 11l-4.9 1.6a2 2 0 0 0-1.3 1.3L12 21l-1.6-4.9a2 2 0 0 0-1.3-1.3L3 11l4.9-1.6a2 2 0 0 0 1.3-1.3z"/>
        </svg>
        <span>Organiser</span>
      </button>

      <button v-if="canUndo" class="undo-layout-btn" @click="undoLayout" title="Revenir à l'organisation précédente">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="1 4 1 10 7 10"></polyline>
          <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
        </svg>
        <span>Annuler</span>
      </button>
    </div>

    <!-- Controls -->
    <div class="canvas-controls">
      <button @click="zoomIn" title="Zoom avant">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
      </button>
      <button @click="resetView" title="Recentrer">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="3"></circle></svg>
      </button>
      <button @click="zoomOut" title="Zoom arrière">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
      </button>
    </div>

    <!-- Légende -->
    <div class="canvas-legend">
      <div class="legend-item">
        <span class="legend-line button"></span>
        <span>Bouton</span>
      </div>
      <div class="legend-item">
        <span class="legend-line redirection"></span>
        <span>Redirection</span>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'FlowGraphCanvas',
  props: {
    chatbotId: {
      type: String,
      required: true
    }
  },
  emits: ['node-click'],
  data() {
    return {
      nodes: [],
      edges: [],
      loading: true,
      error: null,
      transform: {
        x: 0,
        y: 0,
        scale: 1
      },
      isDragging: false,
      lastMousePosition: { x: 0, y: 0 },
      selectedNodeId: null,
      draggingNode: null,
      previousPositions: null, // snapshot avant auto-organisation (pour rollback)
      canUndo: false
    };
  },
  mounted() {
    this.fetchGraph();
    window.addEventListener('mouseup', this.handleMouseUp);
    window.addEventListener('mousemove', this.handleMouseMove);
  },
  beforeUnmount() {
    window.removeEventListener('mouseup', this.handleMouseUp);
    window.removeEventListener('mousemove', this.handleMouseMove);
  },
  methods: {
    async fetchGraph() {
      this.loading = true;
      this.error = null;
      try {
        // Endpoint complet : contient le `flow` de chaque bloc (nécessaire pour les variables)
        const response = await axios.get(`https://jarvis.aipeelo.xyz/api/chatbot/${this.chatbotId}/flow-graph`);
        if (response.data.success) {
          this.nodes = response.data.data.nodes || [];
          this.edges = response.data.data.edges || [];
          this.processGraphData();
          this.$nextTick(() => {
             // Laisse le layout se stabiliser avant de mesurer les nœuds
             requestAnimationFrame(() => this.fitView());
          });
        } else {
          throw new Error(response.data.message || 'Erreur inconnue');
        }
      } catch (err) {
        console.error('Erreur loading graph:', err);
        this.error = "Impossible de charger le graphe.";
      } finally {
        this.loading = false;
      }
    },

    /**
     * Pré-calcule, à partir du flow de chaque bloc :
     *  - les variables référencées/définies dans le bloc ({{...}})  -> puces sur le nœud
     *  - un aperçu texte si le bloc n'a pas de description
     *  - le type de connexion et la variable assignée par chaque association -> flèches
     */
    processGraphData() {
      this.nodes.forEach(node => {
        const vars = this.extractVariables(node.data.flow);
        node.data.variables = vars.map(v => `{{${v}}}`);
        if (!node.data.description) {
          node.data.preview = this.buildPreview(node.data.flow);
        }
      });

      this.edges.forEach(edge => {
        edge.connType = (edge.data && edge.data.connectionType) || 'button';
        edge.assignment = this.getEdgeAssignment(edge);
      });
    },

    extractVariables(flow) {
      if (!flow) return [];
      const re = /\{\{\s*([\w.]+)\s*\}\}/g;
      const set = new Set();
      const json = JSON.stringify(flow);
      let m;
      while ((m = re.exec(json)) !== null) {
        set.add(m[1]);
      }
      return Array.from(set);
    },

    buildPreview(flow) {
      if (!Array.isArray(flow)) return '';
      const textEl = flow.find(el => el && typeof el.message === 'string' && el.message.trim());
      if (!textEl) return '';
      let text = textEl.message.replace(/\*/g, '').replace(/\s+/g, ' ').trim();
      return text.length > 90 ? text.slice(0, 90) + '…' : text;
    },

    /** Retrouve la variable assignée par l'association (payload d'un bouton : {{var}}=valeur). */
    getEdgeAssignment(edge) {
      const src = this.nodes.find(n => n.id === edge.source);
      if (!src || !Array.isArray(src.data.flow)) return '';
      for (const el of src.data.flow) {
        for (const key of Object.keys(el)) {
          const val = el[key];
          if (Array.isArray(val)) {
            const hit = val.find(it => it && it.id === edge.target && it.payload);
            if (hit && String(hit.payload).includes('=')) {
              return String(hit.payload);
            }
          }
        }
      }
      return '';
    },

    getEdgePath(edge) {
      const sourceNode = this.nodes.find(n => n.id === edge.source);
      const targetNode = this.nodes.find(n => n.id === edge.target);
      if (!sourceNode || !targetNode) return '';

      const sourceX = sourceNode.position.x + 300;
      const sourceY = sourceNode.position.y + 45;
      const targetX = targetNode.position.x;
      const targetY = targetNode.position.y + 45;

      const c1x = sourceX + 60;
      const c1y = sourceY;
      const c2x = targetX - 60;
      const c2y = targetY;

      return `M ${sourceX} ${sourceY} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${targetX} ${targetY}`;
    },

    getEdgeMidpoint(edge) {
      const sourceNode = this.nodes.find(n => n.id === edge.source);
      const targetNode = this.nodes.find(n => n.id === edge.target);
      if (!sourceNode || !targetNode) return { x: 0, y: 0 };

      const sx = sourceNode.position.x + 300;
      const sy = sourceNode.position.y + 45;
      const tx = targetNode.position.x;
      const ty = targetNode.position.y + 45;
      const c1x = sx + 60, c1y = sy;
      const c2x = tx - 60, c2y = ty;

      // Point milieu de la courbe de Bézier cubique (t = 0.5)
      const x = 0.125 * sx + 0.375 * c1x + 0.375 * c2x + 0.125 * tx;
      const y = 0.125 * sy + 0.375 * c1y + 0.375 * c2y + 0.125 * ty;
      return { x, y };
    },

    getEdgeLabelStyle(edge) {
      const { x, y } = this.getEdgeMidpoint(edge);
      return { left: `${x}px`, top: `${y}px` };
    },

    nodeTypeLabel(type) {
      const labels = {
        startingPoint: 'Déclencheur',
        default: 'Par défaut',
        commerce: 'Commerce',
        standard: 'Bloc'
      };
      return labels[type] || 'Bloc';
    },

    getNodeIcon(type) {
        const icons = {
            startingPoint: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path></svg>',
            commerce: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path></svg>',
            default: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l1.9 5.8a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-1.9a2 2 0 0 0 1.3-1.3z"></path></svg>',
            standard: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>'
        };
        return icons[type] || icons.standard;
    },

    handleWheel(e) {
      const zoomSensitivity = 0.001;
      const newScale = this.transform.scale - e.deltaY * zoomSensitivity;
      this.transform.scale = Math.min(Math.max(0.1, newScale), 5);
    },

    handleMouseDown(e) {
      if (e.button === 0) {
        this.isDragging = true;
        this.lastMousePosition = { x: e.clientX, y: e.clientY };
      }
    },

    handleMouseMove(e) {
      const deltaX = e.clientX - this.lastMousePosition.x;
      const deltaY = e.clientY - this.lastMousePosition.y;

      if (this.draggingNode) {
        this.draggingNode.position.x += deltaX / this.transform.scale;
        this.draggingNode.position.y += deltaY / this.transform.scale;
      } else if (this.isDragging) {
        this.transform.x += deltaX;
        this.transform.y += deltaY;
      }

      this.lastMousePosition = { x: e.clientX, y: e.clientY };
    },

    handleMouseUp() {
      if (this.draggingNode) {
        this.saveNodePosition(this.draggingNode);
      }
      this.isDragging = false;
      this.draggingNode = null;
    },

    // --- Tactile (mobile) : pan à 1 doigt, pinch-zoom à 2 doigts, tap sur un bloc = l'ouvrir ---
    handleTouchStart(e) {
      // Ne pas intercepter les touches sur les boutons/contrôles du canvas
      if (e.target.closest && e.target.closest('.canvas-controls, .layout-actions, .node-open-btn, .retry-btn')) return;
      if (e.touches.length === 1) {
        const t = e.touches[0];
        this.lastMousePosition = { x: t.clientX, y: t.clientY };
        this.touchTap = { x: t.clientX, y: t.clientY, moved: false, target: e.target };
        this.isDragging = true;
      } else if (e.touches.length === 2) {
        this.isDragging = false;
        if (this.touchTap) this.touchTap.moved = true;
        this.pinchDist = this.getTouchDist(e);
      }
    },
    handleTouchMove(e) {
      if (e.touches.length === 1 && this.isDragging) {
        const t = e.touches[0];
        this.transform.x += t.clientX - this.lastMousePosition.x;
        this.transform.y += t.clientY - this.lastMousePosition.y;
        this.lastMousePosition = { x: t.clientX, y: t.clientY };
        if (this.touchTap && (Math.abs(t.clientX - this.touchTap.x) > 10 || Math.abs(t.clientY - this.touchTap.y) > 10)) {
          this.touchTap.moved = true;
        }
      } else if (e.touches.length === 2 && this.pinchDist) {
        const d = this.getTouchDist(e);
        this.transform.scale = Math.min(Math.max(0.1, this.transform.scale * (d / this.pinchDist)), 5);
        this.pinchDist = d;
      }
    },
    handleTouchEnd(e) {
      // Tap (sans déplacement) sur un bloc -> ouverture directe (pas de double-clic sur mobile)
      if (this.touchTap && !this.touchTap.moved) {
        const nodeEl = this.touchTap.target && this.touchTap.target.closest && this.touchTap.target.closest('.node-item');
        if (nodeEl) {
          const node = this.nodes.find(n => String(n.id) === nodeEl.dataset.nodeId);
          if (node) { e.preventDefault(); this.openNode(node); }
        }
      }
      if (e.touches.length === 0) { this.isDragging = false; this.pinchDist = null; this.touchTap = null; }
    },
    getTouchDist(e) {
      const a = e.touches[0], b = e.touches[1];
      return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    },

    async saveNodePosition(node) {
      if (!node) return;
      try {
        const payload = {
          positions: [
            {
              idblock: node.data.idblock || node.id,
              x: Math.round(node.position.x),
              y: Math.round(node.position.y)
            }
          ]
        };
        await axios.post(`https://jarvis.aipeelo.xyz/api/chatbot/${this.chatbotId}/flow-positions`, payload);
      } catch (error) {
        console.error('Error saving node position:', error);
      }
    },

    startDragNode(e, node) {
      if (e.button === 0) {
        this.draggingNode = node;
        this.selectedNodeId = node.id;
        this.lastMousePosition = { x: e.clientX, y: e.clientY };
      }
    },

    openNode(node) {
      this.$emit('node-click', node);
    },

    zoomIn() {
      this.transform.scale = Math.min(this.transform.scale + 0.2, 5);
    },

    zoomOut() {
      this.transform.scale = Math.max(0.1, this.transform.scale - 0.2);
    },

    resetView() {
      this.fitView();
    },

    /**
     * Réorganise automatiquement les blocs en arbre hiérarchique :
     *  - racine = point d'entrée (nœud startingPoint / Welcome block)
     *  - BFS sur les associations pour assigner une profondeur (couche) à chaque bloc
     *  - chaque couche est une ligne centrée, l'espacement vertical suit la hauteur réelle des blocs
     *  - les blocs non connectés sont placés dans une rangée à part en bas
     */
    autoLayout() {
      if (!this.nodes.length) return;

      // Sauvegarde l'organisation actuelle pour permettre un retour en arrière
      this.previousPositions = {};
      this.nodes.forEach(n => {
        this.previousPositions[n.id] = { x: n.position.x, y: n.position.y };
      });
      this.canUndo = true;

      const H_SPACING = 380;   // espace horizontal entre blocs d'une même couche
      const LAYER_GAP = 90;    // espace vertical entre deux couches
      const DEFAULT_H = 180;

      // Hauteurs réelles des blocs (pour éviter les chevauchements)
      const container = this.$refs.container;
      const nodeEls = container ? container.querySelectorAll('.node-item') : [];
      const heightById = {};
      this.nodes.forEach((n, i) => {
        const el = nodeEls[i];
        heightById[n.id] = el ? el.offsetHeight : DEFAULT_H;
      });

      // Adjacence + degrés entrants
      const children = {};
      const indeg = {};
      this.nodes.forEach(n => { children[n.id] = []; indeg[n.id] = 0; });
      this.edges.forEach(e => {
        if (children[e.source] && (e.target in indeg)) {
          children[e.source].push(e.target);
          indeg[e.target]++;
        }
      });

      // Racines : point(s) d'entrée d'abord, sinon blocs sans parent
      let roots = this.nodes.filter(n => n.data.nodeType === 'startingPoint').map(n => n.id);
      if (!roots.length) roots = this.nodes.filter(n => indeg[n.id] === 0).map(n => n.id);
      if (!roots.length) roots = [this.nodes[0].id];

      // BFS : profondeur + ordre de découverte (enfants groupés sous leur parent)
      const depth = {};
      const order = {};
      const queue = [];
      roots.forEach(r => { if (!(r in depth)) { depth[r] = 0; queue.push(r); } });
      while (queue.length) {
        const id = queue.shift();
        const d = depth[id];
        (order[d] = order[d] || []).push(id);
        for (const c of children[id]) {
          if (!(c in depth)) { depth[c] = d + 1; queue.push(c); }
        }
      }

      // Blocs non connectés (Default, Commerce, îlots) → colonne verticale à droite
      const orphans = this.nodes.filter(n => !(n.id in depth)).map(n => n.id);

      const NODE_W = 320;
      const ORPHAN_GAP = 160;   // écart horizontal entre l'arbre et la colonne des orphelins
      const ORPHAN_VGAP = 40;   // écart vertical entre orphelins

      // Placement de l'arbre connecté, couche par couche (centré autour de x = 0)
      const layers = Object.keys(order).map(Number).sort((a, b) => a - b);
      let currentY = 0;
      let maxRight = 0;
      const posById = {};
      for (const d of layers) {
        const ids = order[d];
        const k = ids.length;
        const startX = -((k - 1) * H_SPACING) / 2;
        let layerMaxH = 0;
        ids.forEach((id, i) => {
          const x = startX + i * H_SPACING;
          posById[id] = { x, y: currentY };
          maxRight = Math.max(maxRight, x + NODE_W);
          layerMaxH = Math.max(layerMaxH, heightById[id] || DEFAULT_H);
        });
        currentY += layerMaxH + LAYER_GAP;
      }

      // Colonne verticale des blocs non connectés, à droite de l'arbre
      if (orphans.length) {
        const orphanX = (layers.length ? maxRight : 0) + ORPHAN_GAP;
        let orphanY = 0;
        orphans.forEach(id => {
          posById[id] = { x: orphanX, y: orphanY };
          orphanY += (heightById[id] || DEFAULT_H) + ORPHAN_VGAP;
        });
      }

      // Applique les nouvelles positions
      this.nodes.forEach(n => {
        const p = posById[n.id];
        if (p) {
          n.position.x = p.x;
          n.position.y = p.y;
        }
      });

      this.$nextTick(() => {
        requestAnimationFrame(() => this.fitView());
      });
      this.saveAllPositions();
    },

    /** Restaure l'organisation d'avant la dernière auto-organisation. */
    undoLayout() {
      if (!this.previousPositions) return;
      this.nodes.forEach(n => {
        const p = this.previousPositions[n.id];
        if (p) {
          n.position.x = p.x;
          n.position.y = p.y;
        }
      });
      this.canUndo = false;
      this.previousPositions = null;
      this.$nextTick(() => {
        requestAnimationFrame(() => this.fitView());
      });
      this.saveAllPositions();
    },

    async saveAllPositions() {
      try {
        const positions = this.nodes.map(n => ({
          idblock: n.data.idblock || n.id,
          x: Math.round(n.position.x),
          y: Math.round(n.position.y)
        }));
        await axios.post(`https://jarvis.aipeelo.xyz/api/chatbot/${this.chatbotId}/flow-positions`, { positions });
      } catch (error) {
        console.error('Error saving positions:', error);
      }
    },

    /**
     * Cadre l'ensemble des nœuds dans le viewport (zoom + position auto),
     * en mesurant la taille réelle de chaque bloc.
     */
    fitView() {
      const container = this.$refs.container;
      if (!container || this.nodes.length === 0) {
        this.centerView();
        return;
      }

      const { width, height } = container.getBoundingClientRect();
      if (!width || !height) {
        this.centerView();
        return;
      }

      const nodeEls = container.querySelectorAll('.node-item');
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

      this.nodes.forEach((node, i) => {
        const el = nodeEls[i];
        const w = el ? el.offsetWidth : 320;
        const h = el ? el.offsetHeight : 180;
        const x = node.position.x;
        const y = node.position.y;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x + w);
        maxY = Math.max(maxY, y + h);
      });

      const graphW = (maxX - minX) || 1;
      const graphH = (maxY - minY) || 1;
      const padding = 100;

      let scale = Math.min(
        (width - padding * 2) / graphW,
        (height - padding * 2) / graphH
      );
      // Évite de trop dézoomer ou de zoomer au-delà du confortable.
      // Mobile : on garde les blocs lisibles (min 0.55) — on explore au doigt (pan/pinch).
      const isMobileView = window.matchMedia('(max-width: 768px)').matches;
      scale = Math.min(Math.max(scale, isMobileView ? 0.55 : 0.1), 1.1);

      let centerX = (minX + maxX) / 2;
      let centerY = (minY + maxY) / 2;

      // Mobile : centrer sur le point d'entrée du flux (le début de l'histoire),
      // le reste se découvre en glissant.
      if (isMobileView) {
        const startIdx = this.nodes.findIndex(n => n.data.nodeType === 'startingPoint');
        if (startIdx !== -1) {
          const startNode = this.nodes[startIdx];
          const el = nodeEls[startIdx];
          centerX = startNode.position.x + ((el ? el.offsetWidth : 320) / 2);
          centerY = startNode.position.y + ((el ? el.offsetHeight : 180) / 2);
        }
      }

      this.transform.scale = scale;
      this.transform.x = width / 2 - scale * centerX;
      this.transform.y = height / 2 - scale * centerY;
    },

    centerView() {
        if (this.$refs.container) {
             const { width, height } = this.$refs.container.getBoundingClientRect();
             this.transform.x = width / 2;
             this.transform.y = height / 2;
             this.transform.scale = 0.8;
        }
    }
  }
};
</script>

<style scoped>
/* ============================================================
   CANVAS — charte Peelo (crème / vert / menthe)
   ============================================================ */
.flow-graph-canvas {
  width: 100%;
  height: 100%;
  background-color: var(--light-bg, #FCFAF7);
  background-image: radial-gradient(rgba(20, 48, 40, 0.07) 1px, transparent 1px);
  background-size: 22px 22px;
  overflow: hidden;
  position: relative;
  cursor: grab;
}

.flow-graph-canvas:active {
  cursor: grabbing;
}

.transform-layer {
  transform-origin: 0 0;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
}

/* ============================================================
   EDGES
   ============================================================ */
.edges-layer {
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  position: absolute;
  pointer-events: none;
  overflow: visible;
}

.edge-path {
  stroke: var(--peelo-green, #143028);
  stroke-width: 2;
  transition: stroke 0.3s;
}

/* Boutons : trait vert plein */
.edge-path.button {
  stroke: var(--peelo-green, #143028);
}

/* Redirections : trait vert pointillé animé */
.edge-path.redirection {
  stroke: rgba(20, 48, 40, 0.55);
  stroke-dasharray: 7 6;
  animation: dash-flow 0.7s linear infinite;
}

@keyframes dash-flow {
  to { stroke-dashoffset: -13; }
}

/* ── Labels d'associations ── */
.edge-labels-layer {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
}

.edge-label-chip {
  position: absolute;
  transform: translate(-50%, -50%);
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  max-width: 220px;
}

.edge-label-text {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 14px;
  font-size: 11.5px;
  font-weight: 600;
  white-space: nowrap;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  background: #ffffff;
  color: var(--peelo-green, #143028);
  border: 1px solid rgba(20, 48, 40, 0.14);
  box-shadow: 0 2px 6px rgba(20, 48, 40, 0.08);
}

.edge-label-chip.redirection .edge-label-text {
  background: var(--peelo-accent, #DAFFD1);
  border-color: rgba(20, 48, 40, 0.18);
  color: var(--peelo-green, #143028);
}

.edge-label-icon {
  display: inline-flex;
  color: var(--peelo-green, #143028);
}

.edge-var {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10.5px;
  font-weight: 600;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  background: rgba(20, 48, 40, 0.06);
  color: rgba(20, 48, 40, 0.85);
  border: 1px dashed rgba(20, 48, 40, 0.2);
  white-space: nowrap;
}

/* ============================================================
   NODES
   ============================================================ */
.node-item {
  position: absolute;
  width: 320px;
  background: var(--light-card-bg, #ffffff);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(20, 48, 40, 0.06);
  border: 1px solid rgba(20, 48, 40, 0.1);
  pointer-events: auto;
  transition: box-shadow 0.2s, transform 0.2s, border-color 0.2s;
  display: flex;
  flex-direction: column;
  cursor: grab;
}

.node-item:active {
  cursor: grabbing;
}

.node-item:hover {
  box-shadow: 0 14px 30px rgba(20, 48, 40, 0.13);
  border-color: rgba(20, 48, 40, 0.2);
  z-index: 10;
}

.node-item.selected {
  border-color: var(--peelo-green, #143028);
  box-shadow: 0 0 0 2px rgba(20, 48, 40, 0.25), 0 14px 30px rgba(20, 48, 40, 0.13);
}

/* Le bloc déclencheur est mis en avant */
.node-item.startingPoint {
  border-color: rgba(20, 48, 40, 0.35);
}

/* Floating Badge */
.node-badge {
  position: absolute;
  top: -12px;
  left: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 11px;
  border-radius: 14px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: var(--peelo-green, #143028);
  background: #ffffff;
  border: 1px solid rgba(20, 48, 40, 0.14);
  box-shadow: 0 2px 6px rgba(20, 48, 40, 0.06);
}

.node-badge-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: rgba(20, 48, 40, 0.4);
}

.node-badge.startingPoint {
  color: var(--peelo-accent, #DAFFD1);
  background: var(--peelo-green, #143028);
  border-color: var(--peelo-green, #143028);
}

.node-badge.startingPoint .node-badge-dot {
  background: var(--peelo-accent, #DAFFD1);
}

.node-badge.commerce .node-badge-dot { background: #b9772a; }

/* Node Header */
.node-header {
  padding: 24px 16px 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.node-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.node-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  flex-shrink: 0;
  background: rgba(20, 48, 40, 0.07);
  color: var(--peelo-green, #143028);
}

.node-icon.startingPoint {
  background: var(--peelo-green, #143028);
  color: var(--peelo-accent, #DAFFD1);
}

.node-icon.commerce {
  background: rgba(185, 119, 42, 0.12);
  color: #b9772a;
}

.node-label {
  font-weight: 700;
  color: var(--light-text, #143028);
  font-size: 15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.node-open-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  color: rgba(20, 48, 40, 0.4);
  padding: 2px 6px;
  border-radius: 6px;
  font-size: 18px;
  line-height: 1;
  flex-shrink: 0;
  transition: all 0.2s;
}

.node-open-btn:hover {
  background: rgba(20, 48, 40, 0.06);
  color: var(--peelo-green, #143028);
}

/* Node Body */
.node-body {
  padding: 0 16px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.node-desc {
  font-size: 13px;
  line-height: 1.5;
  color: var(--light-text-muted, rgba(20, 48, 40, 0.65));
  margin: 0;
}

/* Variables */
.node-vars {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.vars-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(20, 48, 40, 0.45);
}

.vars-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.var-chip {
  padding: 3px 9px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  background: var(--peelo-accent, #DAFFD1);
  color: var(--peelo-green, #143028);
  border: 1px solid rgba(20, 48, 40, 0.12);
}

.var-chip.var-more {
  background: rgba(20, 48, 40, 0.06);
  color: rgba(20, 48, 40, 0.7);
}

.node-footer-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.footer-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 9px;
  background-color: rgba(20, 48, 40, 0.04);
  border: 1px solid rgba(20, 48, 40, 0.08);
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  color: var(--light-text-muted, rgba(20, 48, 40, 0.7));
}

/* Connection Ports */
.node-port {
  position: absolute;
  width: 12px;
  height: 12px;
  background-color: #ffffff;
  border: 2px solid rgba(20, 48, 40, 0.3);
  border-radius: 50%;
  top: 45px;
  transform: translateY(-50%);
  z-index: 2;
}

.node-port.port-in {
  left: -6px;
}

.node-port.port-out {
  right: -6px;
}

.node-item:hover .node-port {
  border-color: var(--peelo-green, #143028);
}

/* ============================================================
   CONTROLS + LEGEND
   ============================================================ */
.layout-actions {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 100;
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

.auto-layout-btn,
.undo-layout-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 15px;
  border-radius: 12px;
  border: none;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s, background 0.2s;
}

.auto-layout-btn {
  background: var(--peelo-green, #143028);
  color: var(--peelo-cream, #FCFAF7);
  box-shadow: 0 4px 14px rgba(20, 48, 40, 0.22);
}

.auto-layout-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(20, 48, 40, 0.3);
}

.auto-layout-btn:active {
  transform: translateY(0);
}

.auto-layout-btn svg {
  color: var(--peelo-accent, #DAFFD1);
}

.undo-layout-btn {
  background: #ffffff;
  color: var(--peelo-green, #143028);
  border: 1px solid rgba(20, 48, 40, 0.14);
  box-shadow: 0 4px 14px rgba(20, 48, 40, 0.1);
}

.undo-layout-btn:hover {
  background: var(--light-hover-bg, rgba(20, 48, 40, 0.04));
  transform: translateY(-1px);
}

.undo-layout-btn:active {
  transform: translateY(0);
}

.canvas-controls {
  position: absolute;
  bottom: 24px;
  left: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 100;
  pointer-events: auto;
}

.canvas-controls button {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid rgba(20, 48, 40, 0.12);
  color: var(--peelo-green, #143028);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(20, 48, 40, 0.08);
  transition: all 0.2s;
}

.canvas-controls button:hover {
  background: var(--peelo-accent, #DAFFD1);
  border-color: rgba(20, 48, 40, 0.2);
}

.canvas-legend {
  position: absolute;
  bottom: 24px;
  right: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(6px);
  border: 1px solid rgba(20, 48, 40, 0.1);
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(20, 48, 40, 0.06);
  z-index: 100;
  pointer-events: auto;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  color: var(--light-text, #143028);
}

.legend-line {
  width: 22px;
  height: 0;
  border-top: 2px solid var(--peelo-green, #143028);
}

.legend-line.redirection {
  border-top-style: dashed;
  border-top-color: rgba(20, 48, 40, 0.55);
}

/* ============================================================
   OVERLAYS
   ============================================================ */
.loading-overlay, .error-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(252, 250, 247, 0.92);
  color: var(--light-text-muted, rgba(20, 48, 40, 0.7));
  z-index: 50;
}

.retry-btn {
  margin-top: 12px;
  padding: 8px 18px;
  border-radius: 10px;
  border: none;
  background: var(--peelo-green, #143028);
  color: var(--peelo-cream, #FCFAF7);
  font-weight: 600;
  cursor: pointer;
}

.spinner {
  border: 3px solid rgba(20, 48, 40, 0.1);
  border-radius: 50%;
  border-top: 3px solid var(--peelo-green, #143028);
  width: 32px;
  height: 32px;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ============================================================
   DARK MODE
   ============================================================ */
:global(.dark-mode) .flow-graph-canvas {
  background-color: var(--dark-bg-deep, #0A1A16);
  background-image: radial-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px);
}

:global(.dark-mode) .node-item {
  background: #15302a;
  border-color: rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

:global(.dark-mode) .node-label {
  color: var(--dark-text, #ffffff);
}

:global(.dark-mode) .node-desc {
  color: var(--dark-text-muted, rgba(255, 255, 255, 0.7));
}

:global(.dark-mode) .node-badge {
  background: #15302a;
  color: var(--peelo-accent, #DAFFD1);
  border-color: rgba(255, 255, 255, 0.12);
}

:global(.dark-mode) .node-icon {
  background: rgba(218, 255, 209, 0.12);
  color: var(--peelo-accent, #DAFFD1);
}

:global(.dark-mode) .edge-path.button {
  stroke: var(--peelo-accent, #DAFFD1);
}

:global(.dark-mode) .edge-path.redirection {
  stroke: rgba(218, 255, 209, 0.5);
}

:global(.dark-mode) .edge-label-text {
  background: #15302a;
  color: var(--dark-text, #ffffff);
  border-color: rgba(255, 255, 255, 0.14);
}

:global(.dark-mode) .edge-label-chip.redirection .edge-label-text {
  background: rgba(218, 255, 209, 0.15);
  color: var(--peelo-accent, #DAFFD1);
}

:global(.dark-mode) .var-chip {
  background: rgba(218, 255, 209, 0.15);
  color: var(--peelo-accent, #DAFFD1);
  border-color: rgba(255, 255, 255, 0.1);
}

:global(.dark-mode) .canvas-controls button,
:global(.dark-mode) .canvas-legend {
  background: #15302a;
  border-color: rgba(255, 255, 255, 0.12);
  color: var(--dark-text, #ffffff);
}

:global(.dark-mode) .canvas-controls button {
  color: var(--peelo-accent, #DAFFD1);
}

:global(.dark-mode) .legend-item {
  color: var(--dark-text, #ffffff);
}

:global(.dark-mode) .loading-overlay,
:global(.dark-mode) .error-overlay {
  background: rgba(10, 26, 22, 0.92);
  color: var(--dark-text-muted, rgba(255, 255, 255, 0.7));
}

:global(.dark-mode) .auto-layout-btn {
  background: var(--peelo-accent, #DAFFD1);
  color: var(--peelo-green, #143028);
}

:global(.dark-mode) .auto-layout-btn svg {
  color: var(--peelo-green, #143028);
}

:global(.dark-mode) .undo-layout-btn {
  background: #15302a;
  color: var(--dark-text, #ffffff);
  border-color: rgba(255, 255, 255, 0.14);
}

:global(.dark-mode) .undo-layout-btn:hover {
  background: rgba(255, 255, 255, 0.06);
}

/* Mobile : contrôles au-dessus de la barre d'onglets Moo/Blocs/Tester (~66px),
   et « Organiser » décalé pour ne pas passer sous le bouton menu du layout. */
@media (max-width: 768px) {
  .canvas-controls { bottom: calc(84px + env(safe-area-inset-bottom, 0px)); left: 12px; }
  .canvas-legend { bottom: calc(84px + env(safe-area-inset-bottom, 0px)); right: 12px; padding: 8px 10px; }
  .layout-actions { top: 12px; left: auto; right: 12px; }
}
</style>
